import type { User } from "../types/Types";
import {
  isBusinessOrExecutiveMembership,
  isPaidPortalMember,
  isYouthOrEarlyStageMembership,
} from "../utils/membershipTier";

/** Business, Executive, Early Stage, or Youth membership names. */
export function hasRecognizedSmbfMembership(
  membershipName?: string | null
): boolean {
  return (
    isYouthOrEarlyStageMembership(membershipName) ||
    isBusinessOrExecutiveMembership(membershipName)
  );
}

function isCanceledMembership(user: User | null | undefined): boolean {
  if (!user) return false;
  const stripe = (user.stripeStatus ?? "").trim().toLowerCase();
  return stripe === "canceled" || stripe === "cancelled";
}

/**
 * Active paid SMBF member (Business / Executive / Early Stage / Youth).
 * Pre-checkout membershipName selection alone does not count.
 */
export function isPaidSmbfMember(user: User | null | undefined): boolean {
  if (!user) return false;
  if (!isPaidPortalMember(user)) return false;
  // Paid with a recognized tier, or paid without a name (legacy rows).
  return (
    !user.membershipName?.trim() ||
    hasRecognizedSmbfMembership(user.membershipName)
  );
}

/**
 * Single source of truth for Explorer product rules (limits, path gates, badges):
 *
 * Explorer = unpaid portal user without an active paid
 * Business / Executive / Early Stage / Youth membership.
 *
 * Not Explorer:
 * - Paid (hasPaid=1 or Stripe active/trialing)
 * - Cancelled former members (inactive-membership flow)
 *
 * Use this everywhere instead of raw `hasPaid === 0` or Explorers-table checks.
 */
export function isUnpaidExplorerUser(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPaidSmbfMember(user)) return false;
  if (isCanceledMembership(user)) return false;
  return true;
}
